/**
 * Timezone utilities for consistent PST handling across the application
 */

export const PST_TIMEZONE = "America/Los_Angeles";

/**
 * Get current date/time in PST
 */
export const getPSTNow = (): Date => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: PST_TIMEZONE }));
};

/**
 * Convert any date to PST
 */
export const toPST = (date: Date | string): Date => {
  const inputDate = typeof date === 'string' ? new Date(date) : date;
  return new Date(inputDate.toLocaleString("en-US", { timeZone: PST_TIMEZONE }));
};

/**
 * Extract date string from database date (which represents PST date as UTC midnight)
 * Database stores PST dates as "2025-10-27T00:00:00.000Z" which represents Oct 27 in PST
 */
export const extractPSTDateString = (dbDate: string): string => {
  return dbDate.split('T')[0]; // Just get YYYY-MM-DD part
};

/**
 * Create a PST date object from database date string for comparison
 * This treats the database date as already being in PST
 */
export const dbDateToPSTDate = (dbDate: string): Date => {
  const dateStr = extractPSTDateString(dbDate);
  return new Date(dateStr + 'T00:00:00'); // Create local date at midnight
};

/**
 * Get PST date string in YYYY-MM-DD format
 */
export const getPSTDateString = (date?: Date): string => {
  const pstDate = date ? toPST(date) : getPSTNow();
  return pstDate.toISOString().split('T')[0];
};

/**
 * Get start of week (Monday) in PST
 */
export const getPSTWeekStart = (date?: Date): Date => {
  const pstDate = date ? toPST(date) : getPSTNow();
  const dayOfWeek = pstDate.getDay(); // 0=Sunday, 1=Monday, etc.
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const startOfWeek = new Date(pstDate);
  startOfWeek.setDate(pstDate.getDate() - daysFromMonday);
  startOfWeek.setHours(0, 0, 0, 0);
  
  return startOfWeek;
};

/**
 * Get end of week (Sunday) in PST
 */
export const getPSTWeekEnd = (date?: Date): Date => {
  const startOfWeek = getPSTWeekStart(date);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  
  return endOfWeek;
};

/**
 * Format date for display in PST. Accepts either a Date object or ISO string
 * (e.g. "2025-11-12T08:00:00.000Z") and preserves the stored timezone offset.
 */
export const formatPSTDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
  let parsedDate: Date;
  
  if (typeof date === 'string') {
    // Handle date-only strings like "2025-11-12" to avoid timezone shift
    if (!date.includes('T')) {
      // For date-only strings, create date at noon UTC to avoid timezone edge cases
      parsedDate = new Date(date + 'T12:00:00.000Z');
    } else {
      // For full ISO strings, parse directly
      parsedDate = new Date(date);
    }
  } else {
    parsedDate = date;
  }

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: PST_TIMEZONE
  };

  return parsedDate.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};
